import { memo } from "react";
import { AxisBottom, AxisLeft, Orientation } from "@visx/axis";
import { GridRows } from "@visx/grid";
import { RectClipPath } from "@visx/clip-path";
import MemoizedAnimatedPoints from "./AnimatedPoints";

import { text, dataViz, fontFamily, dataVizTypography } from "@atlas-int/tokens";
import styled from "@emotion/styled";
const AxisGElement = styled.g`
  pointer-events: none;
  & text {
    font-family: '${fontFamily.base}', sans-serif;
    font-size: ${dataVizTypography.axisLabel};
    fill: ${dataViz.axisText};
  }
`;
import type { ScatterplotBaseProps } from "./scatterplotTypes";

const TICK_TRANSITION = "transform 400ms ease, opacity 400ms ease";

const ScatterplotBase = memo(
  ({
    width,
    height,
    yPadding,
    zoomedXScale,
    zoomedYScale,
    zoomTransform,
    isDragging,
    onDragStart,
    onDragMove,
    onDragEnd,
    filteredData,
    ySelection,
    metaDataLookup,
    resetZoom: _resetZoom,
    highlightedItem,
    setHighlightedItem,
    productClass,
    totalExportLookup,
    setHoveredItem,
    countryData,
  }: ScatterplotBaseProps) => {
    return (
      <>
        <RectClipPath
          id="zoom-clip"
          width={Math.max(0, width - yPadding)}
          height={Math.max(0, height - yPadding / 2)}
          x={yPadding}
        />
        <g clipPath="url(#zoom-clip)">
          <rect
            width={Math.max(0, width - yPadding)}
            x={yPadding}
            height={Math.max(0, height - yPadding / 2)}
            rx={14}
            fill="transparent"
            onTouchStart={onDragStart}
            onTouchMove={onDragMove}
            onTouchEnd={onDragEnd}
            onMouseDown={onDragStart}
            onMouseMove={onDragMove}
            onMouseUp={onDragEnd}
            onMouseLeave={onDragEnd}
            style={{
              touchAction: "none",
              cursor: isDragging ? "grabbing" : "grab",
            }}
          />
        </g>
        <AxisGElement>
          <AxisBottom
            scale={zoomedXScale}
            tickLabelProps={() => ({
              dy: "0.5em",
              textAnchor: "middle",
              style: { transition: TICK_TRANSITION },
            })}
            tickLineProps={{
              style: { transition: TICK_TRANSITION },
            }}
            orientation={Orientation.bottom}
            top={height - yPadding / 2}
          />
        </AxisGElement>
        <g transform={`translate(${yPadding})`}>
          <AxisGElement>
            <AxisLeft
              scale={zoomedYScale}
              tickLabelProps={() => ({
                dx: "-0.25em",
                dy: "0.25em",
                textAnchor: "end",
                style: { transition: TICK_TRANSITION },
              })}
              tickLineProps={{
                style: { transition: TICK_TRANSITION },
              }}
            />
          </AxisGElement>
          <GridRows
            scale={zoomedYScale}
            width={width - yPadding}
            height={height}
            stroke={dataViz.overlayLight}
            lineStyle={{ transition: "transform 400ms ease" }}
          />
        </g>
        <g clipPath="url(#zoom-clip)">
          {ySelection === "pci" &&
            countryData?.eci != null &&
            Number.isFinite(countryData.eci) && (
              <>
                <text
                  y={zoomedYScale(countryData.eci!) + 20}
                  x={yPadding + 6}
                  style={{
                    fontWeight: 600,
                    fill: dataViz.textSecondary,
                    transition: "y 400ms ease",
                  }}
                >
                  ECI ({productClass}) {countryData.eci!.toFixed(2)}
                </text>
                <line
                  strokeDasharray="3 2"
                  y1={zoomedYScale(countryData.eci!)}
                  y2={zoomedYScale(countryData.eci!)}
                  x1="0"
                  x2={width}
                  strokeWidth={3}
                  style={{
                    stroke: text.secondary,
                    transition: "y1 400ms ease, y2 400ms ease",
                  }}
                  vectorEffect="non-scaling-stroke"
                />
              </>
            )}
          <g transform={zoomTransform}>
            {width && (
              <MemoizedAnimatedPoints
                filteredData={filteredData as unknown as Parameters<typeof MemoizedAnimatedPoints>[0]["filteredData"]}
                ySelection={ySelection}
                metaDataLookup={metaDataLookup as Parameters<typeof MemoizedAnimatedPoints>[0]["metaDataLookup"]}
                highlightedItem={highlightedItem}
                setHighlightedItem={setHighlightedItem}
                productClass={productClass}
                totalExportLookup={totalExportLookup}
                setHoveredItem={setHoveredItem}
              />
            )}
          </g>
        </g>
      </>
    );
  },
);

export default ScatterplotBase;
